import { ReactElement, ReactNode } from 'react'
import { Avatar, Box, Card, CardContent, Grid, Typography } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'

export interface StatItem {
  label: string
  value: ReactNode
  icon: ReactElement
  color?: string
}

export interface StatCardRowProps {
  items: StatItem[]
  layout: 'flex' | 'grid'
  sx?: SxProps<Theme>
  gridItem?: { xs?: number; sm?: number; md?: number }
  valueVariant?: 'h4' | 'h6'
  valueFontWeight?: number
}

const StatCardRow = ({
  items,
  layout,
  sx,
  gridItem = { xs: 12, sm: 6, md: 3 },
  valueVariant = 'h6',
  valueFontWeight,
}: StatCardRowProps) => {
  if (layout === 'flex') {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 3,
          flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' },
          '& > *': {
            flex: { xs: '1 1 calc(50% - 4px)', sm: '1 1 calc(33.333% - 7px)', md: '1 1 0' },
            minWidth: { xs: 'calc(50% - 4px)', sm: 'calc(33.333% - 7px)', md: 0 },
          },
          ...sx,
        }}
      >
        {items.map((item) => (
          <Card key={item.label} sx={{ borderRadius: 1.5 }}>
            <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: item.color, width: 32, height: 32 }}>{item.icon}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem', display: 'block', lineHeight: 1.2 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    )
  }

  return (
    <Grid container spacing={3} sx={sx}>
      {items.map((item) => (
        <Grid item key={item.label} xs={gridItem.xs} sm={gridItem.sm} md={gridItem.md}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: item.color }}>{item.icon}</Avatar>
                <Box>
                  <Typography variant={valueVariant} sx={valueFontWeight ? { fontWeight: valueFontWeight } : undefined}>
                    {item.value}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.label}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

export default StatCardRow
